// Phase 1 facet derivation for the Careers Library filter system.
// Tags are auto-derived from existing Career fields + careerMeta. No manual tagging required.

import type { Career } from "@/data/careers";
import { getCareerMeta } from "@/data/careerMeta";

export type EntryBarrier =
  | "no-degree"
  | "entry-level"
  | "fast-start"
  | "apprenticeship"
  | "requires-degree"
  | "advanced-qual";

export type WorkStyleTag = "remote" | "hybrid" | "on-site" | "flexible" | "shift";

export interface CareerFacets {
  /** Min number from startingSalary range, e.g. 28000. 0 if unparseable. */
  salaryMin: number;
  /** Max number from seniorSalary range (or startingSalary if missing). */
  salaryMax: number;
  /** Lower bound of the starting-salary range, used for filter bucket. */
  startingMin: number;
  entryBarriers: EntryBarrier[];
  workStyles: WorkStyleTag[];
  isSelfEmployable: boolean;
}

const NUM_RE = /£?\s*([0-9]{1,3}(?:,[0-9]{3})+|[0-9]+)\s*k?/gi;

/** Parse "£28,000 - £40,000" -> [28000, 40000]. Handles "k" shorthand and trailing notes. */
export function parseSalaryRange(input?: string | null): [number, number] {
  if (!input) return [0, 0];
  const matches: number[] = [];
  let m: RegExpExecArray | null;
  NUM_RE.lastIndex = 0;
  while ((m = NUM_RE.exec(input)) !== null) {
    const raw = m[1].replace(/,/g, "");
    let n = parseInt(raw, 10);
    if (isNaN(n)) continue;
    // "60k" -> 60000
    if (/k/i.test(m[0]) && n < 1000) n = n * 1000;
    matches.push(n);
  }
  if (matches.length === 0) return [0, 0];
  if (matches.length === 1) return [matches[0], matches[0]];
  return [Math.min(...matches), Math.max(...matches)];
}

function deriveEntryBarriers(career: Career): EntryBarrier[] {
  const out = new Set<EntryBarrier>();
  const meta = getCareerMeta(career.name);
  const sy = meta.studyYears;
  const degreeText = (career.degreeRequired ?? "").toLowerCase();
  const timeText = (career.timeToEntry ?? "").toLowerCase();

  const requiresDegree =
    degreeText.includes("required") ||
    degreeText.includes("essential") ||
    degreeText.includes("must") ||
    sy >= 3;

  const noDegree =
    degreeText.includes("not required") ||
    degreeText.includes("not strict") ||
    degreeText.includes("helpful but not") ||
    degreeText.includes("optional") ||
    sy < 3;

  if (noDegree && !degreeText.includes("essential")) out.add("no-degree");
  if (requiresDegree && degreeText.includes("required") && !degreeText.includes("not required")) {
    out.add("requires-degree");
  }
  if (sy >= 5) out.add("advanced-qual");
  if (sy <= 1) {
    out.add("fast-start");
    out.add("entry-level");
  }
  if (
    timeText.includes("apprentice") ||
    degreeText.includes("apprentice") ||
    (sy >= 2 && sy <= 4 && noDegree)
  ) {
    out.add("apprenticeship");
  }
  return Array.from(out);
}

function deriveWorkStyles(career: Career): WorkStyleTag[] {
  const env = (career.workEnvironment ?? "").toLowerCase();
  const out = new Set<WorkStyleTag>();
  if (env.includes("remote")) out.add("remote");
  if (env.includes("hybrid")) out.add("hybrid");
  if (
    env.includes("office") ||
    env.includes("on-site") ||
    env.includes("on site") ||
    env.includes("workshop") ||
    env.includes("clinic") ||
    env.includes("hospital") ||
    env.includes("site") ||
    env.includes("kitchen") ||
    env.includes("classroom") ||
    env.includes("studio")
  ) {
    out.add("on-site");
  }
  if (env.includes("flexible") || env.includes("freelance") || career.selfEmployable) {
    out.add("flexible");
  }
  if (env.includes("shift") || env.includes("rota") || env.includes("nights")) {
    out.add("shift");
  }
  if (out.size === 0) out.add("on-site");
  return Array.from(out);
}

export function getCareerFacets(career: Career): CareerFacets {
  const [startMin, startMax] = parseSalaryRange(career.startingSalary);
  const [, seniorMax] = parseSalaryRange(career.seniorSalary ?? career.midSalary ?? career.startingSalary);
  return {
    salaryMin: startMin,
    salaryMax: Math.max(seniorMax, startMax),
    startingMin: startMin,
    entryBarriers: deriveEntryBarriers(career),
    workStyles: deriveWorkStyles(career),
    isSelfEmployable: !!(career.selfEmployable || career.selfEmploymentOnly),
  };
}

export const ENTRY_BARRIER_LABELS: Record<EntryBarrier, string> = {
  "no-degree": "No degree",
  "entry-level": "Entry-level",
  "fast-start": "Fast start",
  apprenticeship: "Apprenticeship",
  "requires-degree": "Degree required",
  "advanced-qual": "Advanced qualification",
};

export const WORK_STYLE_LABELS: Record<WorkStyleTag, string> = {
  remote: "Remote",
  hybrid: "Hybrid",
  "on-site": "On-site",
  flexible: "Flexible hours",
  shift: "Shift-based",
};

export const SALARY_MIN = 0;
export const SALARY_MAX = 100000;
